import logoStandard from "@/images/logo/logo_top.png?h=60&format=png&inline&vite-imagetools";
import logoBusiness from "@/images/logo/logo_top_business.png?h=60&format=png&inline&vite-imagetools";
import logoBusinessXs from "@/images/logo/logo_top_business_xs.png?h=60&format=png&inline&vite-imagetools";
import logoPro from "@/images/logo/logo_top_pro.png?h=60&format=png&inline&vite-imagetools";
import logoProXs from "@/images/logo/logo_top_pro_xs.png?h=60&format=png&inline&vite-imagetools";
import logoStandardXs from "@/images/logo/logo_top_xs.png?h=60&format=png&inline&vite-imagetools";

import {
    Bell,
    BellPlus,
    Boxes,
    CircleQuestionMark,
    Cookie,
    FloatingFooter,
    FloatingNavbar,
    Globe,
    Headset,
    HeartHandshake,
    Layers,
    Mail,
    MailOpen,
    MonitorSmartphone,
    Omega,
    Scale,
    ShieldUser,
    Siren,
    Squirrel,
    Terminal,
    Users,
    Wifi,
} from "@disasterscience/scaffoldy";
import { useCallback, useEffect, useState } from "react";
import { FaAppStore, FaFacebook, FaGooglePlay, FaInstagram } from "react-icons/fa6";
import { Outlet } from "react-router-dom";
import { Flag } from "./components/Flag";
import { ScrollToTop } from "./components/ScrollToTop";
import { Zaraz } from "./components/Zaraz";
import { useAppContext } from "./contexts/AppContext";
import { useSearch } from "./contexts/SearchContext";
import { BfGroupName } from "./lib/api/models";
import { Friendly } from "./lib/friendly";
import { Toast } from "./lib/toast";
import { NamedRoute, RouteMap } from "./Routes";

interface IRootProps {
    children?: React.ReactNode;
}

enum LogoSelected {
    Standard,
    Pro,
    Business,
}

export const Root = (props: IRootProps) => {
    const {
        isInGroup,
        online,
        isLoggedIn,
        definedRegions,
        eulaAccepted,
        bestRegionAvailable,
        timemachineActive,
        isDeveloper,
        isModerator,
        isManagement,
        isEnterprise,
        isOrgManager,
        isRoot,
        user,
        org,
        instanceRendering,
    } = useAppContext();
    const navbarRegions = definedRegions().filter((r) => r.showInNav);
    const [logoSelected, setLogoSelected] = useState<LogoSelected>(LogoSelected.Standard);
    const { handleSearch, handleSelect } = useSearch();
    const isBusiness = isLoggedIn && isInGroup([BfGroupName.business, BfGroupName.enterprise]);
    const isPro = isLoggedIn && isInGroup([BfGroupName.pro]);
    const isLicencing =
        isLoggedIn &&
        isInGroup([BfGroupName.licencing, BfGroupName.business, BfGroupName.enterprise]);

    const determineLogo = useCallback((): LogoSelected => {
        if (isBusiness) {
            return LogoSelected.Business;
        }

        if (isPro) {
            return LogoSelected.Pro;
        }

        return LogoSelected.Standard;
    }, [isBusiness, isPro]);

    useEffect(() => {
        if (!online) {
            Toast.error("You are currently offline. Check your internet connection.");
        }
    }, [online]);

    useEffect(() => {
        setLogoSelected(determineLogo());
    }, [determineLogo]);

    const logo =
        logoSelected === LogoSelected.Standard
            ? logoStandard
            : logoSelected === LogoSelected.Pro
              ? logoPro
              : logoBusiness;

    const logoSm =
        logoSelected === LogoSelected.Standard
            ? logoStandardXs
            : logoSelected === LogoSelected.Pro
              ? logoProXs
              : logoBusinessXs;

    const navbarRegionItems = navbarRegions.map((r) => {
        return {
            title: r.friendly,
            href: `/?region=${r.name}`,
            description: `View ${r.name}`,
            image: <Flag region={r.name} />,
        };
    });

    const bestRegion = Friendly.Region(bestRegionAvailable());

    return (
        <>
            <ScrollToTop element="#main" />
            <FloatingNavbar
                logo={logo}
                logoSm={logoSm}
                searchPlaceholder={`Search ${bestRegion}`}
                searchPlaceholderOpen="Search incidents, locations or lat,lons"
                onSearch={eulaAccepted ? handleSearch : undefined}
                onSearchSelect={handleSelect}
                warningHint={!online || timemachineActive !== null}
                showDarkMode={isDeveloper()}
                items={[
                    {
                        title: "Map",
                        description: "Map views",
                        itemsTitle: "Regions",
                        items: [
                            {},
                            ...navbarRegionItems,
                            {},
                            {
                                title: "Symbol Legend",
                                href: RouteMap.Path(NamedRoute.Legend),
                                description: "View the symbol legend",
                                image: <Omega size={18} className="tw:mr-1" />,
                            },
                        ],
                        show: instanceRendering.showMap,
                    },
                    {
                        title: "Notifications",
                        description: "Manage Notifications",
                        show: isLoggedIn && instanceRendering.showNotifications,
                        items: [
                            {
                                title: "Create Notification",
                                href: RouteMap.Path(NamedRoute.NotificationsSearch),
                                show: instanceRendering.showNotificationsCreate,
                                description: "Create a new notification",
                                image: <BellPlus size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Inbox",
                                href: RouteMap.Path(NamedRoute.NotificationsList),
                                description: "View recent messages",
                                show: instanceRendering.showNotificationsInbox,
                                image: <Mail size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Settings",
                                href: RouteMap.Path(NamedRoute.NotificationsSettings),
                                description: "View settings",
                                show: instanceRendering.showNotificationsSettings,
                                image: <Bell size={18} className="tw:mr-1" />,
                            },
                        ],
                    },
                    {
                        title: "Notifications",
                        href: RouteMap.Path(NamedRoute.Login),
                        description: "Manage Notifications",
                        show: !isLoggedIn && instanceRendering.showNotifications,
                    },
                    {
                        title: "My Organisation",
                        description: "Manage Organisation",
                        show:
                            (isBusiness || isManagement() || isLicencing) &&
                            instanceRendering.showOrganisation,
                        itemsTitle: org?.name,
                        items: [
                            {
                                title: "Create Organisation",
                                href: RouteMap.Path(NamedRoute.OrgCreate),
                                description: "Create a new organisation",
                                show: (isBusiness || isLicencing) && !org,
                                image: <Boxes size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Layers",
                                href: RouteMap.Path(NamedRoute.CustomLayersList),
                                description: "Manage custom layers",
                                show: isBusiness,
                                image: <Layers size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Incidents",
                                href: RouteMap.Path(NamedRoute.ListDataSources),
                                description: "Manage incidents",
                                show: isManagement(),
                                image: <Siren size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Groups and Licences",
                                href: RouteMap.Path(NamedRoute.LicencesList),
                                description: "Manage Licencing",
                                show: isLicencing,
                                image: <ShieldUser size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Users",
                                href: RouteMap.Path(NamedRoute.OrgUsers),
                                description: "Manage organisation users",
                                show: !!org && (isOrgManager() || isRoot()),
                                image: <Users size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "GIS and Tokens",
                                description: "Manage GIS and API tokens",
                                href: "/ux/account/developers",
                                show: isDeveloper() || isEnterprise(),
                                image: <Globe size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Settings",
                                href: RouteMap.Path(NamedRoute.OrgSettings),
                                description: "Manage organisation settings",
                                show: (isBusiness || isLicencing) && !!org,
                                image: <Boxes size={18} className="tw:mr-1" />,
                            },
                        ],
                    },
                    {
                        title: "Media",
                        href: RouteMap.Path(NamedRoute.ModerationQueue),
                        description: "View media library",
                        show: instanceRendering.showMedia && isModerator(),
                    },
                    {
                        title: "Plans",
                        href: RouteMap.Path(NamedRoute.Plans),
                        description: "Sign up or upgrade",
                        show: instanceRendering.showPlans && !(isPro || isBusiness),
                    },
                    {
                        title: "Feedback",
                        href: RouteMap.Path(NamedRoute.Feedback),
                        description: "Send feedback",
                        show: instanceRendering.showFeedback,
                    },
                    {
                        title: "Login",
                        href: RouteMap.Path(NamedRoute.Login),
                        description: "Login",
                        show: !isLoggedIn && instanceRendering.showLogin,
                    },
                    {
                        title: "More",
                        href: "/",
                        description: "More Links",
                        collapsed: false,
                        show: instanceRendering.showMore,
                        items: [
                            {
                                title: "Diagnostics",
                                href: "/ux/diag",
                                description: "Diagnostics",
                                show: isDeveloper() || instanceRendering.showMoreDiagnostics,
                                image: <Terminal size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Plans",
                                href: RouteMap.Path(NamedRoute.Plans),
                                description: "Sign up or upgrade",
                                show: instanceRendering.showPlans && (isPro || isBusiness),
                                image: <Omega size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Frequently Asked Questions",
                                href: "https://dstr.to/JQmvDY",
                                description: "View FAQs",
                                show: instanceRendering.showMoreFaq,
                                image: <CircleQuestionMark size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Supported Devices",
                                href: "/ux/supported",
                                description: "View supported devices",
                                show: instanceRendering.showMoreSupportedDevices,
                                image: <MonitorSmartphone size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Welcome & Disclaimer",
                                href: "/ux/welcome",
                                description: "View welcome message",
                                show: instanceRendering.showMoreWelcome,
                                image: <Headset size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Work with Us",
                                href: "/ux/contact",
                                description: "Contact us",
                                show: instanceRendering.showMoreWorkWithUs,
                                image: <Squirrel size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "About",
                                href: "/ux/about",
                                description: "About us",
                                show: instanceRendering.showMoreAbout,
                                image: <HeartHandshake size={18} className="tw:mr-1" />,
                            },
                            {
                                show:
                                    instanceRendering.showMoreSystemStatus ||
                                    instanceRendering.showMorePrivacyPolicy ||
                                    instanceRendering.showMoreTermsOfService,
                            },
                            {
                                title: "System Status",
                                href: "https://dstr.to/2MPJ9R",
                                description: "View system status",
                                show: instanceRendering.showMoreSystemStatus,
                                image: <Wifi size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Privacy Policy",
                                href: "https://docs.bushfire.io/privacy",
                                description: "View privacy policy",
                                show: instanceRendering.showMorePrivacyPolicy,
                                image: <Cookie size={18} className="tw:mr-1" />,
                            },
                            {
                                title: "Terms of Service",
                                href: "https://docs.bushfire.io/terms",
                                description: "View terms of service",
                                show: instanceRendering.showMoreTermsOfService,
                                image: <Scale size={18} className="tw:mr-1" />,
                            },
                            {
                                show: instanceRendering.showMoreSocialLinks,
                            },
                            {
                                title: (
                                    <>
                                        <FaInstagram size={18} className="tw:mr-2" />
                                        <FaFacebook size={18} className="tw:mr-2" />
                                        <FaGooglePlay size={18} className="tw:mr-2" />
                                        <FaAppStore size={18} className="tw:mr-2" />
                                    </>
                                ),
                                href: "https://linktr.ee/bushfireio",
                                description: "Check out our socials",
                                show: instanceRendering.showMoreSocialLinks,
                            },
                        ],
                    },
                ]}
                name={user ? user.name : undefined}
                email={user ? user.email : undefined}
                link={user ? RouteMap.Path(NamedRoute.Account) : undefined}
            />
            <Zaraz />
            <div className="tw:min-h-dvh tw:h-svh tw:flex tw:flex-col tw:overflow-hidden">
                <main className="tw:flex-1 tw:overflow-y-auto tw:w-full" id="main">
                    {props.children ? props.children : <Outlet />}
                </main>
                <FloatingFooter
                    items={[
                        {
                            title: "Map",
                            href: "/",
                            description: "Map views",
                            image: <Globe />,
                        },
                        {
                            title: "Inbox",
                            href: isLoggedIn
                                ? RouteMap.Path(NamedRoute.NotificationsList)
                                : RouteMap.Path(NamedRoute.Login),
                            description: "View recent messages",
                            image: <MailOpen />,
                        },
                        {
                            title: "Notifications",
                            href: isLoggedIn
                                ? RouteMap.Path(NamedRoute.NotificationsSettings)
                                : RouteMap.Path(NamedRoute.Login),
                            description: "View recent messages",
                            image: <Bell />,
                        },
                        {
                            title: "Layers",
                            href: RouteMap.Path(NamedRoute.CustomLayersList),
                            description: "Manage custom layers",
                            image: <Layers />,
                            show: isBusiness,
                        },
                        // {
                        //     title: "Incidents",
                        //     href: RouteMap.Path(NamedRoute.ListDataSources),
                        //     description: "Manage incidents",
                        //     image: <TriangleAlert />,
                        //     show: isManagement,
                        // },
                    ]}
                />
            </div>
        </>
    );
};
